/**
 * IkusPanel — GEN-EKB-009: los iKus de un KB de empresa. Lista, alta manual (título,
 * resumen, tipo de la lista cerrada, texto), ficha con su cabecera, edición (cada cambio
 * crea una versión nueva) y versiones anteriores visibles.
 *
 * Lo que decide algo lo decide el kernel: el tipo es una lista que llega del kernel, el
 * nivel DIKW viene deducido, y el KB es obligatorio (sin KB no se guarda).
 */

import { useCallback, useEffect, useState } from 'react'
import { anadirIkuAKb, buscarKbs, crearIku, desenlazarIku, editarIku, getIku, getVersionIku, listIkus, quitarIkuDeKb, retirarIku } from '../api/client.js'

const NIVEL = { datos: 'Datos', informacion: 'Información', conocimiento: 'Conocimiento' }
const ETIQUETA = { confidencial: 'Confidencial', interna: 'Interna', publica: 'Pública' }
const MENSAJES = {
  kb_obligatorio: 'Elige el KB: un iKu no se guarda sin KB.',
  kb_retirado: 'El KB está retirado.',
  tipo_invalido: 'Elige un tipo de la lista.',
  tipo_no_disponible: 'Ese tipo estará disponible en la versión 1.0.1.',
  titulo_invalido: 'El título es obligatorio.',
  texto_invalido: 'El texto es obligatorio.',
  vigencia_invalida: 'La vigencia debe ser una fecha.',
  conflicto_version: 'Alguien editó este iKu a la vez: recarga y repite.',
  contenido_no_disponible: 'El almacén de contenido aún no está disponible en esta instalación.',
  ekb_no_disponible: 'La base de conocimiento aún no está disponible en esta instalación.',
  forbidden: 'Tu perfil no puede hacer esto.',
  ya_en_kb: 'El iKu ya está en ese KB.',
  kb_no_encontrado: 'Ese KB no existe o no puedes usarlo.',
  enlace_no_encontrado: 'Ese iKu ya no está enlazado en este KB.',
  ultimo_contenedor: 'Un iKu no se quita de su último KB. Si ya no hace falta, retira el iKu.',
  iku_retirado: 'El iKu está retirado: ya no se edita ni se mueve de KB.',
}
const mensaje = err => MENSAJES[err?.code] || err?.message || 'No se ha podido completar la operación.'
// Leyenda de los tipos (§3.2.1). Mapa de datos e indicador están en el modelo pero no se crean hasta la 1.0.1.
const LEYENDA_TIPOS = [
  ['Hecho', 'algo que es así en tu empresa.'],
  ['Procedimiento', 'cómo se hace algo, paso a paso.'],
  ['Decisión', 'algo que se decidió y por qué.'],
  ['Lección', 'lo que se aprendió de un caso.'],
  ['Referencia', 'enlace a una web o documento; lo que se lea al seguirlo no tiene autoridad.'],
  ['Mapa de datos e Indicador', 'dónde está un dato o cómo se calcula; disponibles en la 1.0.1.'],
]
const AVISO_RESUMEN = 'Si lo dejas vacío, el Escriba lo rellenará'
const ESTADO = { vigente: 'Vigente', retirado: 'Retirado', propuesto: 'Propuesto' }
const fecha = iso => (iso ? new Date(iso).toLocaleString() : '')

export function FormIku({ inicial, kbId, tipos, onGuardar, onCancelar, etiquetaBoton }) {
  const [titulo, setTitulo] = useState(inicial?.titulo ?? '')
  const [resumen, setResumen] = useState(inicial?.resumen ?? '')
  const [tipo, setTipo] = useState(inicial?.tipo ?? 'hecho')
  const [vigencia, setVigencia] = useState(inicial?.vigencia ?? '')
  const [texto, setTexto] = useState(inicial?.texto ?? '')
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)

  async function enviar(e) {
    e.preventDefault()
    setGuardando(true)
    setError(null)
    try {
      await onGuardar({ kb_id: kbId, titulo: titulo.trim(), resumen: resumen.trim(), tipo, vigencia, texto: texto.trim() })
    } catch (err) {
      setError(mensaje(err))
      setGuardando(false)
    }
  }

  return (
    <form className="equipo-nuevo ikus-form" onSubmit={enviar}>
      <label>Título <input required maxLength={200} autoComplete="off" value={titulo} onChange={e => setTitulo(e.target.value)} /></label>
      <label>
        Resumen (opcional)
        <textarea rows={2} maxLength={600} value={resumen} onChange={e => setResumen(e.target.value)} aria-describedby="ikus-aviso-resumen" />
        <small id="ikus-aviso-resumen" className="ikus-ayuda">{AVISO_RESUMEN}</small>
      </label>
      <label>
        Tipo
        <select required value={tipo} onChange={e => setTipo(e.target.value)}>
          <option value="">Elige un tipo…</option>
          {tipos.map(t => (
            <option key={t.id} value={t.id} disabled={t.disponible === false}>
              {t.disponible === false ? `${t.etiqueta} (disponible en 1.0.1)` : t.etiqueta}
            </option>
          ))}
        </select>
      </label>
      <ul className="ikus-leyenda" aria-label="Tipos de iKu">
        {LEYENDA_TIPOS.map(([t, d]) => <li key={t}><strong>{t}</strong>: {d}</li>)}
      </ul>
      <label>Vigente hasta (opcional) <input type="date" value={vigencia} onChange={e => setVigencia(e.target.value)} /></label>
      <label>Texto <textarea required rows={8} value={texto} onChange={e => setTexto(e.target.value)} /></label>
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      <div className="equipo-nuevo__acciones">
        <button type="submit" className="btn btn--primary" disabled={guardando}>{etiquetaBoton}</button>
        <button type="button" className="btn btn--ghost" onClick={onCancelar} disabled={guardando}>Cancelar</button>
      </div>
    </form>
  )
}

function Cabecera({ iku }) {
  const filas = [
    ['Id', iku.id], ['Título', iku.titulo], ['Resumen', iku.resumen || 'Sin resumen (lo rellenará el Escriba)'],
    ['Tipo', iku.tipo_etiqueta], ['Nivel DIKW', NIVEL[iku.nivel] ?? iku.nivel],
    ['Vigencia', iku.vigencia || 'Sin fecha'], ['Origen', iku.origen],
    ['Versión', iku.version], ['Tokens', iku.tokens],
    ...((iku.enlaces ?? []).length ? [['Enlazado en', iku.enlaces.map(c => c.nombre).join(', ')]] : []), // GEN-EKB-043
    ['Estado', ESTADO[iku.estado] ?? iku.estado], ...(iku.retirado_en ? [['Retirado el', fecha(iku.retirado_en)]] : []), ['Etiqueta', ETIQUETA[iku.etiqueta] ?? iku.etiqueta],
  ]
  return (
    <dl className="ikus-cabecera" aria-label="Cabecera del iKu">
      {filas.map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v}</dd></div>))}
    </dl>
  )
}

// GEN-EKB-011: el mismo iKu en varios KBs. La etiqueta efectiva es la más restrictiva de sus KBs;
// quitarlo de un KB que la rebajaría queda pendiente de que lo confirme el titular de ese KB.
export function KbsDelIku({ iku, onCambio }) {
  const [q, setQ] = useState('')
  const [hallados, setHallados] = useState([])
  const [aviso, setAviso] = useState(null)
  const [nota, setNota] = useState(null)
  const [confirmando, setConfirmando] = useState(false)
  const pendientes = iku.quitas_pendientes ?? []

  useEffect(() => {
    const t = q.trim()
    if (!t) { setHallados([]); return undefined }
    let vivo = true
    buscarKbs(t, iku.id).then(r => vivo && setHallados(r.kbs ?? [])).catch(() => vivo && setHallados([]))
    return () => { vivo = false }
  }, [q, iku.id, iku.contenedores.length])

  async function anadir(kb) {
    setAviso(null); setNota(null); setConfirmando(false)
    try {
      await anadirIkuAKb(iku.id, kb.id)
      setQ('')
      setNota(`Añadido a «${kb.nombre}». Es el mismo iKu, sin copia.`)
      await onCambio()
    } catch (err) { setAviso(mensaje(err)) }
  }

  async function quitar(c) {
    setAviso(null); setNota(null); setConfirmando(false)
    try {
      const r = await quitarIkuDeKb(iku.id, c.id)
      setNota(r.resultado === 'pendiente'
        ? `Quitarlo de «${c.nombre}» rebaja su etiqueta efectiva: queda pendiente hasta que lo confirme el titular de ese KB.`
        : `Quitado de «${c.nombre}».`)
      await onCambio()
    } catch (err) {
      setAviso(mensaje(err))
    }
  }

  async function retirar() {
    setAviso(null); setNota(null)
    try {
      await retirarIku(iku.id)
      await onCambio()
    } catch (err) {
      setConfirmando(false)
      setAviso(mensaje(err))
    }
  }

  return (
    <>
      <h3>KBs</h3>
      <p className="equipo-estado">Etiqueta efectiva: <strong>{ETIQUETA[iku.etiqueta] ?? iku.etiqueta}</strong> (la más restrictiva de sus KBs).</p>
      <ul className="kbs-traza" aria-label="KBs del iKu">
        {iku.contenedores.map(c => (
          <li key={c.id}>
            {c.nombre}{c.estado === 'retirado' ? ' (retirado)' : ''}{' '}
            {pendientes.includes(c.id)
              ? <em>quitar pendiente de confirmación del titular</em>
              : <button type="button" className="btn btn--ghost" aria-label={`Quitar de ${c.nombre}`} onClick={() => quitar(c)}>Quitar de este KB</button>}
          </li>
        ))}
      </ul>
      {aviso && <p className="equipo-estado" role="alert">{aviso}</p>}
      {nota && <p className="equipo-estado" role="status">{nota}</p>}
      {confirmando ? (
        <p className="equipo-estado" role="alertdialog" aria-label="Confirmar retirada">
          <span className="dk-rotulo">Retirar iKu · Unidad incremental de conocimiento</span>
          ¿Retirar este iKu? Deja de usarse, pero no se borra: seguirás viéndolo como retirado, con sus versiones.{' '}
          <button type="button" className="btn btn--danger" onClick={retirar}>Sí, retirar</button>{' '}
          <button type="button" className="btn btn--ghost" onClick={() => setConfirmando(false)}>Cancelar</button>
        </p>
      ) : (
        <button type="button" className="btn btn--danger" onClick={() => setConfirmando(true)}>Retirar iKu</button>
      )}
      <label>
        Añadir a otro KB
        <input type="search" role="combobox" aria-expanded={hallados.length > 0} aria-label="Buscar KB" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} placeholder="Escribe parte del nombre…" value={q} onChange={e => setQ(e.target.value)} />
      </label>
      {q.trim() && hallados.length === 0 && <p className="equipo-estado">Ningún KB activo coincide.</p>}
      {hallados.length > 0 && (
        <ul className="kbs-traza" role="listbox" aria-label="KBs encontrados">
          {hallados.map(k => (
            <li key={k.id} role="option" aria-selected="false">
              <button type="button" className="btn btn--ghost" onClick={() => anadir(k)}>{k.nombre} · {ETIQUETA[k.etiqueta] ?? k.etiqueta}</button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function FichaIku({ ikuId, tipos, onVolver, onCambio }) {
  const [iku, setIku] = useState(null)
  const [vista, setVista] = useState(null) // versión antigua en pantalla
  const [editando, setEditando] = useState(false)
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setIku((await getIku(ikuId)).iku)
      setVista(null)
    } catch (err) {
      setError(mensaje(err))
    }
  }, [ikuId])
  useEffect(() => { cargar() }, [cargar])

  async function verVersion(n) {
    setError(null)
    try {
      setVista((await getVersionIku(ikuId, n)).iku)
    } catch (err) {
      setError(mensaje(err))
    }
  }

  if (!iku) {
    return (
      <section className="mec-visor">
        <button type="button" className="btn btn--ghost" onClick={onVolver}>← iKus</button>
        {error ? <p className="equipo-estado" role="alert">{error}</p> : <p className="equipo-estado">Cargando…</p>}
      </section>
    )
  }
  const mostrado = vista ?? iku
  return (
    <section className="mec-visor">
      <button type="button" className="btn btn--ghost" onClick={onVolver}>← iKus</button>
      <h2>{iku.titulo}</h2>
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      {editando ? (
        <FormIku
          inicial={iku}
          tipos={tipos}
          etiquetaBoton="Guardar (nueva versión)"
          onCancelar={() => setEditando(false)}
          onGuardar={async f => {
            const { kb_id: _kb, ...cambios } = f
            await editarIku(iku.id, cambios)
            setEditando(false)
            await cargar()
            onCambio()
          }}
        />
      ) : (
        <>
          {vista && (
            <p className="equipo-estado" role="status">
              Estás viendo la versión {vista.version}, anterior a la vigente ({iku.version}).{' '}
              <button type="button" className="btn btn--ghost" onClick={() => setVista(null)}>Volver a la vigente</button>
            </p>
          )}
          <Cabecera iku={mostrado} />
          <h3>Texto</h3>
          <pre className="ikus-texto">{mostrado.texto}</pre>
          {iku.estado === 'retirado' && (
            <p className="equipo-estado" role="status">Este iKu está retirado{iku.retirado_en ? ` desde ${fecha(iku.retirado_en)}` : ''}: ya no se usa, no se edita ni cambia de KB. Sus versiones siguen aquí.</p>
          )}
          {!vista && iku.estado === 'vigente' && <button type="button" className="btn btn--primary" onClick={() => setEditando(true)}>Editar</button>}
          {!vista && iku.estado === 'vigente' && <KbsDelIku iku={iku} onCambio={async () => { await cargar(); onCambio() }} />}
          <h3>Versiones</h3>
          <ul className="kbs-traza" aria-label="Versiones del iKu">
            {[...iku.versiones].reverse().map(v => (
              <li key={v.version}>
                Versión {v.version}{v.version === iku.version ? ' (vigente)' : ''} · {fecha(v.creado_en)} · {v.autor}{' '}
                {v.version !== mostrado.version && (
                  <button type="button" className="btn btn--ghost" onClick={() => verVersion(v.version)}>Ver versión {v.version}</button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

export default function IkusPanel({ kb, onVolver }) {
  const [datos, setDatos] = useState(null)
  const [modo, setModo] = useState(null) // null | 'nuevo'
  const [abierto, setAbierto] = useState(null)
  const [estado, setEstado] = useState('vigente') // GEN-EKB-012: vigente | retirado | todos
  const [aviso, setAviso] = useState(null)

  const cargar = useCallback(async () => {
    try {
      setDatos(await listIkus(kb.id, estado))
    } catch (err) {
      setDatos({ error: mensaje(err) })
    }
  }, [kb.id, estado])
  useEffect(() => { cargar() }, [cargar])

  if (abierto) {
    return <FichaIku ikuId={abierto} tipos={datos?.tipos ?? []} onVolver={() => { setAbierto(null); cargar() }} onCambio={cargar} />
  }
  const ikus = datos?.ikus ?? []
  // GEN-EKB-043: quitar el enlace no retira el iKu, que sigue en su KB de origen
  async function quitarEnlace(i) {
    setAviso(null)
    try { await desenlazarIku(i.id, kb.id); await cargar() } catch (err) { setAviso(mensaje(err)) }
  }
  return (
    <section className="mec-visor">
      <button type="button" className="btn btn--ghost" onClick={onVolver}>← KBs</button>
      <h2>iKus de «{kb.nombre}»</h2>
      <p className="equipo-estado">Cada iKu guarda una idea concreta. Nace vigente, en versión 1, dentro de este KB.</p>
      {modo === 'nuevo' && (
        <FormIku
          kbId={kb.id}
          tipos={datos?.tipos ?? []}
          etiquetaBoton="Crear iKu"
          onCancelar={() => setModo(null)}
          onGuardar={async f => { await crearIku(f); setModo(null); await cargar() }}
        />
      )}
      {!modo && kb.estado === 'activo' && (
        <button type="button" className="btn btn--primary" onClick={() => setModo('nuevo')} disabled={!datos || datos.error}>Nuevo iKu</button>
      )}
      <label>
        Estado
        <select aria-label="Filtrar por estado" value={estado} onChange={e => setEstado(e.target.value)}>
          <option value="vigente">Vigentes</option>
          <option value="retirado">Retirados</option>
          <option value="todos">Todos</option>
        </select>
      </label>
      {aviso && <p className="equipo-estado" role="alert">{aviso}</p>}
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {datos && !datos.error && ikus.length === 0 && (
        <p className="equipo-estado">
          {estado === 'retirado' ? 'Este KB no tiene iKus retirados.' : estado === 'vigente' ? 'Este KB aún no tiene iKus vigentes.' : 'Este KB aún no tiene iKus.'}
        </p>
      )}
      {ikus.length > 0 && (
        <table className="kbs-tabla">
          <thead><tr><th>Título</th><th>Resumen</th><th>Tipo</th><th>Versión</th><th>Estado</th><th>Etiqueta</th><th /></tr></thead>
          <tbody>
            {ikus.map(i => (
              <tr key={i.id}>
                <td>
                  {i.titulo ?? '(contenido no disponible)'}
                  {i.enlazado && (
                    <> <span className="ikus-enlazado">Enlazado</span> <a href={`#/saber/kb/${i.origen_kb.id}`} aria-label={`KB de origen: ${i.origen_kb.nombre}`}>de «{i.origen_kb.nombre}»</a></>
                  )}
                </td>
                <td className={i.resumen ? undefined : 'ikus-sin-resumen'}>{i.resumen_mostrado}</td>
                <td>{i.tipo_etiqueta}</td>
                <td>{i.version}</td>
                <td>{ESTADO[i.estado] ?? i.estado}{i.retirado_en ? ` · ${fecha(i.retirado_en)}` : ''}</td>
                <td>{ETIQUETA[i.etiqueta] ?? i.etiqueta}</td>
                <td>
                  <button type="button" className="btn btn--ghost" onClick={() => setAbierto(i.id)}>Abrir</button>
                  {i.enlazado && <button type="button" className="btn btn--ghost" aria-label={`Quitar el enlace de ${i.titulo ?? 'este iKu'}`} onClick={() => quitarEnlace(i)}>Quitar enlace</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
